import { dependsOnVariable } from './evaluate';
import { type Node } from './parse';

/**
 * Differentiating an expression, symbolically.
 *
 * ## Why symbolic rather than numeric
 *
 * A variation table is a statement about *signs*, and a sign is exactly what a numeric
 * derivative is worst at. `(f(x+h) - f(x-h)) / 2h` near a stationary point is a small
 * difference of two nearly equal numbers: the value is right to a few digits and its **sign**
 * is noise. A table built on that gets `x^3` wrong at the origin — the one point the table is
 * about — and gets it wrong differently depending on the step size.
 *
 * The rules below are the ones in a school textbook, and they are exact wherever the function
 * is. That matters more than speed here: the derivative is built once when the table is made,
 * not once per sample.
 *
 * ## `null` means "no derivative", and it is not a failure
 *
 * `floor`, `ceil`, `round`, `sign`, `min` and `max` are not differentiable, and no amount of
 * effort makes them so. Returning `null` lets the caller say *this function has no variation
 * table* rather than produce one from a derivative that does not exist. The alternative —
 * differentiating the pieces and hoping — is how a tool ends up asserting that a staircase is
 * increasing.
 *
 * `abs` is the near miss: it has a derivative everywhere except at zero, and `sign(u)·u'` is
 * that derivative. The single bad point behaves like any other point the sampler finds no
 * value at, so it costs nothing to allow.
 */

const ZERO: Node = Object.freeze({ kind: 'number', value: 0 });
const ONE: Node = Object.freeze({ kind: 'number', value: 1 });

/**
 * The derivative of `node` with respect to `x`, or `null` when it has none.
 *
 * The result is simplified on the way out, which is not cosmetic: the chain rule multiplies by
 * `u'` at every level, and `u'` is `1` for the commonest case of all. Left alone, `sin(x)`
 * differentiates to `cos(x) * 1` and `x^3` to `3 * x^(3-1) * 1`, and by the third nesting the
 * tree is mostly multiplications by one.
 */
export function differentiate(node: Node): Node | null {
  const raw = derive(node);
  return raw === null ? null : simplify(raw);
}

function derive(node: Node): Node | null {
  switch (node.kind) {
    case 'number':
      return ZERO;

    case 'variable':
      return ONE;

    case 'unary': {
      const inner = derive(node.operand);
      return inner === null ? null : negate(inner);
    }

    case 'binary':
      return deriveBinary(node);

    case 'call':
      return deriveCall(node);
  }
}

function deriveBinary(node: Node & { kind: 'binary' }): Node | null {
  const left = derive(node.left);
  const right = derive(node.right);
  if (left === null || right === null) return null;

  switch (node.operator) {
    case '+':
      return add(left, right);

    case '-':
      return subtract(left, right);

    // (uv)' = u'v + uv'
    case '*':
      return add(multiply(left, node.right), multiply(node.left, right));

    // (u/v)' = (u'v − uv') / v²
    case '/':
      return divide(
        subtract(multiply(left, node.right), multiply(node.left, right)),
        power(node.right, number(2))
      );

    case '^':
      return derivePower(node, left, right);
  }
}

/**
 * Three power rules, and which one applies is decided by where `x` appears.
 *
 * The general form `u^v · (v'·ln u + v·u'/u)` is correct for all three, and using it
 * everywhere would be a mistake: it introduces `ln u`, so differentiating `x^2` would produce
 * an expression undefined for every negative `x`. The derivative would then have a domain
 * narrower than the function it came from, and the table would report a gap that is not there.
 *
 * So the two special cases are not optimisations. They are the only forms that keep the
 * derivative defined where the function is.
 */
function derivePower(node: Node & { kind: 'binary' }, left: Node, right: Node): Node {
  const baseHasX = dependsOnVariable(node.left);
  const exponentHasX = dependsOnVariable(node.right);

  // u^n, n constant: n·u^(n−1)·u'
  if (!exponentHasX) {
    return multiply(multiply(node.right, power(node.left, subtract(node.right, ONE))), left);
  }

  // a^v, a constant: a^v·ln(a)·v'
  if (!baseHasX) {
    return multiply(multiply(node, call('ln', node.left)), right);
  }

  // u^v in full.
  return multiply(
    node,
    add(multiply(right, call('ln', node.left)), divide(multiply(node.right, left), node.left))
  );
}

function deriveCall(node: Node & { kind: 'call' }): Node | null {
  const argument = node.args[0];
  if (argument === undefined) return null;

  const inner = derive(argument);
  if (inner === null) return null;

  /** The chain rule's outer factor, or `null` when there is no derivative to take. */
  const outer = ((): Node | null => {
    switch (node.name) {
      case 'sin':
        return call('cos', argument);
      case 'cos':
        return negate(call('sin', argument));
      // 1/cos²u rather than 1 + tan²u: the two agree wherever both are defined, and this one
      // says plainly where it is not — at the vertical asymptotes, where cos is zero.
      case 'tan':
        return divide(ONE, power(call('cos', argument), number(2)));
      case 'asin':
        return divide(ONE, call('sqrt', subtract(ONE, power(argument, number(2)))));
      case 'acos':
        return negate(divide(ONE, call('sqrt', subtract(ONE, power(argument, number(2))))));
      case 'atan':
        return divide(ONE, add(ONE, power(argument, number(2))));
      case 'sinh':
        return call('cosh', argument);
      case 'cosh':
        return call('sinh', argument);
      case 'tanh':
        return divide(ONE, power(call('cosh', argument), number(2)));
      case 'sqrt':
        return divide(ONE, multiply(number(2), call('sqrt', argument)));
      case 'abs':
        return call('sign', argument);
      case 'exp':
        return call('exp', argument);
      case 'ln':
        return divide(ONE, argument);
      case 'log':
        return divide(ONE, multiply(argument, number(Math.LN10)));

      // Not differentiable, and pretending otherwise is how a tool claims a staircase rises.
      case 'floor':
      case 'ceil':
      case 'round':
      case 'sign':
      case 'min':
      case 'max':
        return null;
    }
  })();

  return outer === null ? null : multiply(outer, inner);
}

/* ------------------------------------------------------------------ simplification */

/**
 * Arithmetic the reader would have done in their head.
 *
 * Bounded and local on purpose: one bottom-up pass applying identities about 0 and 1, folding
 * constants, and nothing else. It is not a computer algebra system and must not grow into one —
 * `sin²+cos²` staying as it is costs nothing here, while a rewriting engine that loops or
 * changes a domain would cost a wrong table.
 */
export function simplify(node: Node): Node {
  switch (node.kind) {
    case 'number':
    case 'variable':
      return node;

    case 'unary': {
      const operand = simplify(node.operand);
      if (operand.kind === 'number') return number(-operand.value);
      // −(−u) = u. Double negation is what the quotient rule produces most often.
      if (operand.kind === 'unary') return operand.operand;
      return { kind: 'unary', operand };
    }

    case 'call':
      return { kind: 'call', name: node.name, args: node.args.map(simplify) };

    case 'binary':
      return simplifyBinary(node);
  }
}

function simplifyBinary(node: Node & { kind: 'binary' }): Node {
  const left = simplify(node.left);
  const right = simplify(node.right);

  const leftValue = left.kind === 'number' ? left.value : null;
  const rightValue = right.kind === 'number' ? right.value : null;

  /*
   * Constants are folded except for a division by zero.
   *
   * `1/0` is `Infinity`, which is a perfectly good float and a terrible node: it would be
   * *baked into* the tree, so every later evaluation returns it and the sampler reads a pole
   * that the original expression never had. Left unfolded, the division happens at evaluation
   * time, where `evaluateAt` already answers `null`.
   */
  if (leftValue !== null && rightValue !== null) {
    const folded = fold(node.operator, leftValue, rightValue);
    if (folded !== null) return number(folded);
  }

  switch (node.operator) {
    case '+':
      if (leftValue === 0) return right;
      if (rightValue === 0) return left;
      break;

    case '-':
      if (rightValue === 0) return left;
      if (leftValue === 0) return simplify({ kind: 'unary', operand: right });
      break;

    case '*':
      if (leftValue === 0 || rightValue === 0) return ZERO;
      if (leftValue === 1) return right;
      if (rightValue === 1) return left;
      if (leftValue === -1) return simplify({ kind: 'unary', operand: right });
      if (rightValue === -1) return simplify({ kind: 'unary', operand: left });
      break;

    case '/':
      if (rightValue === 1) return left;
      // 0/v stays: it is zero only where v is defined and non-zero, and the table reads the
      // difference between "zero here" and "no value here".
      break;

    case '^':
      if (rightValue === 1) return left;
      // u^0 is 1 for every u the expression can reach; 0^0 is the one exception, and an
      // expression that raises a literal zero to a literal zero has already been folded.
      if (rightValue === 0) return ONE;
      break;
  }

  return { kind: 'binary', operator: node.operator, left, right };
}

function fold(operator: '+' | '-' | '*' | '/' | '^', left: number, right: number): number | null {
  const value = ((): number => {
    switch (operator) {
      case '+':
        return left + right;
      case '-':
        return left - right;
      case '*':
        return left * right;
      case '/':
        return left / right;
      case '^':
        return Math.pow(left, right);
    }
  })();

  return Number.isFinite(value) ? value : null;
}

/* --------------------------------------------------------------------- constructors */

function number(value: number): Node {
  return { kind: 'number', value };
}

function negate(operand: Node): Node {
  return { kind: 'unary', operand };
}

function add(left: Node, right: Node): Node {
  return { kind: 'binary', operator: '+', left, right };
}

function subtract(left: Node, right: Node): Node {
  return { kind: 'binary', operator: '-', left, right };
}

function multiply(left: Node, right: Node): Node {
  return { kind: 'binary', operator: '*', left, right };
}

function divide(left: Node, right: Node): Node {
  return { kind: 'binary', operator: '/', left, right };
}

function power(left: Node, right: Node): Node {
  return { kind: 'binary', operator: '^', left, right };
}

function call(
  name: 'sin' | 'cos' | 'sqrt' | 'ln' | 'exp' | 'sign' | 'cosh' | 'sinh',
  argument: Node
): Node {
  return { kind: 'call', name, args: [argument] };
}
